import React, { createContext, useContext, useState, useEffect } from 'react';

const ChartPreferencesContext = createContext();

export const ChartPreferencesProvider = ({ children }) => {
  const [showLabels, setShowLabels] = useState(() => {
    const saved = localStorage.getItem('chartShowLabels');
    return saved ? JSON.parse(saved) : false;
  });

  // 'full' = número completo, 'k' = miles, 'M' = millones, 'auto' = automático
  const [numberFormat, setNumberFormat] = useState(() => {
    const saved = localStorage.getItem('chartNumberFormat');
    return saved ? JSON.parse(saved) : 'auto';
  });

  // 0, 1, o 2 decimales
  const [decimals, setDecimals] = useState(() => {
    const saved = localStorage.getItem('chartDecimals');
    return saved !== null ? JSON.parse(saved) : 1;
  });

  useEffect(() => {
    localStorage.setItem('chartShowLabels', JSON.stringify(showLabels));
  }, [showLabels]);

  useEffect(() => {
    localStorage.setItem('chartNumberFormat', JSON.stringify(numberFormat));
  }, [numberFormat]);

  useEffect(() => {
    localStorage.setItem('chartDecimals', JSON.stringify(decimals));
  }, [decimals]);

  const toggleLabels = () => setShowLabels(prev => !prev);

  // Función para formatear números según las preferencias
  const formatChartValue = (value, prefix = '$') => {
    if (typeof value !== 'number' || isNaN(value)) return '';
    
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    
    let formatted;
    let suffix = '';
    
    if (numberFormat === 'full') {
      formatted = absValue.toLocaleString('es-MX', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0 
      });
    } else if (numberFormat === 'k') {
      formatted = (absValue / 1000).toFixed(decimals);
      suffix = 'k';
    } else if (numberFormat === 'M') {
      formatted = (absValue / 1000000).toFixed(decimals);
      suffix = 'M';
    } else {
      // Auto: decide basándose en el valor
      if (absValue >= 1000000) {
        formatted = (absValue / 1000000).toFixed(decimals);
        suffix = 'M';
      } else if (absValue >= 1000) {
        formatted = (absValue / 1000).toFixed(decimals);
        suffix = 'k';
      } else {
        formatted = absValue.toFixed(0);
      }
    }
    
    return `${sign}${prefix}${formatted}${suffix}`;
  };

  // Función para formatear números en tablas (usa las preferencias de formato)
  const formatTableValue = (value, currency = 'MXN') => {
    if (typeof value !== 'number' || isNaN(value)) return '-';
    if (value === 0) return '-';
    
    const prefix = currency === 'USD' ? 'US$' : '$';
    return formatChartValue(value, prefix);
  };

  // Función para formatear números de volumen (sin símbolo de moneda)
  const formatVolumeValue = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '-';
    if (value === 0) return '-';
    
    return formatChartValue(value, '');
  };

  return (
    <ChartPreferencesContext.Provider value={{ 
      showLabels, 
      setShowLabels, 
      toggleLabels,
      numberFormat,
      setNumberFormat,
      decimals,
      setDecimals,
      formatChartValue,
      formatTableValue,
      formatVolumeValue
    }}>
      {children}
    </ChartPreferencesContext.Provider>
  );
};

export const useChartPreferences = () => {
  const context = useContext(ChartPreferencesContext);
  if (!context) {
    throw new Error('useChartPreferences must be used within ChartPreferencesProvider');
  }
  return context;
};
